// Geometry for rotating a photo INSIDE a fixed rectangular slot (grid cell)
// while keeping the slot fully covered — no corner gaps (Canva-style).
//
// Shared by all three render pipelines so they rotate identically:
//   - editor   → pdfMirrorBox (AlbumEditorPage.tsx)
//   - preview  → PreviewPageCard (AlbumFlowPage.tsx, via pdfMirrorBox)
//   - PDF      → computeTranslateDrawParams (pdfGenerator.ts)
//
// The plain cover-fit scale is max(slotW/imgW, slotH/imgH). When the image is
// rotated by θ about the slot centre, its corners sweep inward and would expose
// the slot background unless we scale the image up. The image's drawn size must
// contain the slot's extent as seen in the image's own (un-rotated) frame —
// i.e. the slot rotated by −θ, whose axis-aligned bounding box is
//   (W·|cosθ| + H·|sinθ|) × (W·|sinθ| + H·|cosθ|).

/** Absolute cover scale needed so an imgW×imgH image rotated by `rotationDeg`
 *  fully covers a slotW×slotH slot. At 0° this equals max(slotW/imgW, slotH/imgH). */
export function coverScaleForRotation(
  slotW: number,
  slotH: number,
  imgW: number,
  imgH: number,
  rotationDeg: number,
): number {
  const r = (rotationDeg * Math.PI) / 180;
  const c = Math.abs(Math.cos(r));
  const s = Math.abs(Math.sin(r));
  const needW = slotW * c + slotH * s;
  const needH = slotW * s + slotH * c;
  return Math.max(needW / imgW, needH / imgH);
}

/** Factor (≥ 1) to multiply the un-rotated cover scale by so the image still
 *  covers the slot after rotating by `rotationDeg`. Scale-invariant — it depends
 *  only on the slot/image aspect ratios + the angle — so callers can pass real
 *  dimensions (PDF) or aspect ratios (editor passes slotAR, 1, imgW, imgH).
 *  Clamped to ≥ 1 so rotating never shrinks the image below its cover fit. */
export function rotationCoverGrowth(
  slotW: number,
  slotH: number,
  imgW: number,
  imgH: number,
  rotationDeg: number,
): number {
  if (slotW <= 0 || slotH <= 0 || imgW <= 0 || imgH <= 0) return 1;
  const base = Math.max(slotW / imgW, slotH / imgH);
  if (base <= 0) return 1;
  const rot = coverScaleForRotation(slotW, slotH, imgW, imgH, rotationDeg);
  return Math.max(1, rot / base);
}
